/* eslint-disable brace-style */

import format from 'date-fns/format'

// initially lifted from https://github.com/sonnyp/JSON8
// extended to also accept & work with Yatte proxy objects and primitives

// type.js -- https://github.com/sonnyp/JSON8/blob/master/packages/json8/lib/type.js
const OBJECT = 'object'
const ARRAY = 'array'
const NULL = 'null'
const STRING = 'string'
const BOOLEAN = 'boolean'
const NUMBER = 'number'

function type (obj) {
  const t = typeof obj

  if (t === BOOLEAN || t === STRING) return t
  else if (t === NUMBER && isFinite(obj)) return NUMBER
  else if (t === OBJECT) {
    if (Array.isArray(obj)) return ARRAY
    else if (global.Set && obj instanceof Set) return ARRAY
    else if (global.Map && obj instanceof Map) return OBJECT
    else if (obj === null) return NULL
    else if (t === OBJECT) return OBJECT
  }
}

function value (obj) {
  if (obj) {
    if (typeof obj === OBJECT && obj.constructor === Date) {
      return format(obj, 'yyyy-MM-dd')
    }
  }
  return obj
}

// encode.js -- https://github.com/sonnyp/JSON8/blob/master/packages/pointer/lib/encode.js
function encode (tokens, separator) {
  let pointer = ''
  const sep =
    typeof separator === 'string' && separator.length > 0 ? separator : '/'

  for (let i = 0, len = tokens.length; i < len; i++) {
    pointer += sep + escape(String(tokens[i]), sep)
  }

  return pointer
}

// escape.js -- https://github.com/sonnyp/JSON8/blob/master/packages/pointer/lib/escape.js
function escape (token, separator) {
  const sep =
    typeof separator === 'string' && separator.length > 0 ? separator : '/'
  let escaped = ''
  for (let i = 0, length = token.length; i < length; i++) {
    const l = token.charAt(i)
    if (l === '~') escaped += '~0'
    else if (l === sep) escaped += '~1'
    else escaped += l
  }
  return escaped
}

// equal.js -- https://github.com/sonnyp/JSON8/blob/master/packages/json8/lib/equal.js
function toArray (set) {
  const array = []
  set.forEach(function (item) {
    array.push(item)
  })
  return array
}

function toObject (map) {
  const object = Object.create(null)
  map.forEach(function (value, key) {
    object[key] = value
  })
  return object
}

function equal (a, b) {
  const ta = type(a)
  const tb = type(b)

  if (ta !== tb) return false

  const t = ta

  switch (t) {
    case NUMBER:
      if (a === 0 && 1 / a === -Infinity) return b === 0 && 1 / b === -Infinity
      return a === b
    case STRING:
    case NULL:
    case BOOLEAN:
      return a === b
    default:
  }

  let i, l
  if (t === ARRAY) {
    if (global.Set) {
      if (a instanceof Set) a = toArray(a)
      if (b instanceof Set) b = toArray(b)
    }
    if (a.length !== b.length) return false
    for (i = 0, l = a.length; i < l; i++) if (!equal(a[i], b[i])) return false
    return true
  }

  if (t === OBJECT) {
    if (global.Map) {
      if (a instanceof Map) a = toObject(a)
      if (b instanceof Map) b = toObject(b)
    }
    const keys = Object.keys(a)
    if (keys.length !== Object.keys(b).length) return false
    for (i = 0, l = keys.length; i < l; i++) {
      const key = keys[i]
      if (b.hasOwnProperty && !Object.prototype.hasOwnProperty.call(b, key)) return false
      if (!equal(b[key], a[key])) return false
    }
    return true
  }

  return true
}

// diff.js -- https://github.com/sonnyp/JSON8/blob/master/packages/patch/lib/diff.js
function diff (a, b, typeName, typeDefs, pre) {
  let patches = []
  if (pre && pre.length && pre[pre.length - 1] === '_link') return patches // client-side diff ignores _link properties
  const prefix = pre || []

  a = value(a)
  b = value(b)
  let at = type(a)
  let bt = type(b)

  if (bt !== at) {
    if (at === OBJECT && Object.keys(a).length === 0) at = NULL // treat "empty" objects same as NULL
    if (bt === OBJECT && Object.keys(b).length === 0) bt = NULL // treat "empty" objects same as NULL
    if ((at === undefined || at === NULL || a === '') && (bt === undefined || bt === NULL || b === '')) {
      // ignore difference
    } else if (at === undefined) {
      patches.push({ op: 'add', path: encode(prefix), value: b })
    } else if (at === ARRAY && (bt === undefined || bt === NULL)) {
      // ignore difference, I think?
    } else if (!a && !b) {
      // ignore differences between falsy values
    } else {
      patches.push({ op: 'replace', path: encode(prefix), value: b })
    }
    return patches
  } else if (bt !== ARRAY && bt !== OBJECT) {
    if (!equal(a, b)) {
      patches.push({ op: 'replace', path: encode(prefix), value: b })
    }
    return patches
  }

  if (a === b) return patches

  const typeDef = typeDefs[typeName]
  if (Array.isArray(b)) { // both are arrays
    // FIXME let's be smarter about array diffing
    if (a.length === 0 && b.length === 0) return patches
    if (equal(a, b)) return patches
    if (typeDef && typeDef.type === 'object') {
      const keyName = typeDef.key || 'id$'
      const leftInB = [...b]
      for (const itemA of a) {
        if (itemA) {
          if (typeof itemA === 'object') {
            const keyVal = itemA[keyName]
            const bi = leftInB.findIndex(obj => obj && (obj[keyName] === keyVal))
            if (bi >= 0) {
              const itemB = leftInB.splice(bi, 1)[0]
              patches = patches.concat(diff(itemA, itemB, typeName, typeDefs, prefix.concat([keyVal])))
            } else {
              patches.push({ op: 'remove', path: encode(prefix.concat([keyVal])) })
            }
          } else { // typeof itemA !== 'object'
            const bi = leftInB.findIndex(it => it === itemA)
            if (bi >= 0) {
              leftInB.splice(bi, 1)
            } else {
              patches.push({ op: 'remove', path: encode(prefix.concat([itemA])) })
            }
          }
        }
      }
      for (const itemB of leftInB) {
        if (itemB) {
          if (typeof itemB === 'object') {
            let keyVal = itemB[keyName]
            if (isNaN(keyVal) || keyVal === null || keyVal === undefined) {
              keyVal = '-' // append new entry -- does not have id yet
            }
            patches.push({ op: 'add', path: encode(prefix.concat([keyVal])), value: itemB })
          } else { // typeof itemB !== 'object'
            patches.push({ op: 'add', path: encode(prefix.concat(['-'])), value: itemB })
          }
        }
      }
    } else {
      patches.push({ op: 'replace', path: encode(prefix), value: b })
    }
  }
  else if (bt === OBJECT) { // both are objects
    let i, l
    const aCopy = { ...a }
    const bKeys = Object.keys(b)
    for (i = 0, l = bKeys.length; i < l; i++) {
      const bk = bKeys[i]
      const vardef = typeDef.variables.find(d => d.name === bk)
      if (vardef) {
        if (vardef.name === 'id$' && typeDef.key && typeDef.key !== 'id$') {
          // ignore id$ changes when there is some other key explicitly specified!
        } else {
          // normal case
          patches = patches.concat(diff(a[bk], b[bk], vardef.typeName, typeDefs, prefix.concat([bk])))
        }
      }
      if (bk in aCopy) {
        delete aCopy[bk]
      }
    }
    // unmatched properties remaining in aCopy may need to be deleted/removed
    const aKeys = Object.keys(aCopy)
    for (i = 0, l = aKeys.length; i < l; i++) {
      const ak = aKeys[i]
      if (b[ak] !== undefined) continue
      // so now we know ak is not defined in b, and it IS defined in a... does it need to be noted as a removal?
      const vardef = typeDef.variables.find(d => d.name === ak)
      if (vardef) {
        if (aCopy[ak] !== null && aCopy[ak] !== '') {
          patches.push({ op: 'remove', path: encode(prefix.concat([ak])) })
        }
      }
    }
  }

  return patches
}

export {
  diff,
}
